import type { PostHog } from "posthog-js";

type PostHogClient =
  | Pick<PostHog, "capture" | "displaySurvey">
  | null
  | undefined;
type DisplaySurveyOptions = NonNullable<
  Parameters<NonNullable<Pick<PostHog, "displaySurvey">["displaySurvey"]>>[1]
>;
type DisplaySurveyPopoverOptions = Extract<
  DisplaySurveyOptions,
  { position?: unknown }
>;
type EventPropertyValue = string | number | boolean;

const CUSTOM_INSIGHTS_FEEDBACK_SURVEY_OPTIONS: DisplaySurveyPopoverOptions = {
  displayType: "popover" as DisplaySurveyPopoverOptions["displayType"],
  ignoreConditions: true,
  ignoreDelay: true,
};

type OpenCustomInsightsFeedbackSurveyOptions = {
  anchorSelector?: string;
};

function buildCustomInsightsFeedbackSurveyOptions(
  options?: OpenCustomInsightsFeedbackSurveyOptions
): DisplaySurveyPopoverOptions {
  if (!options?.anchorSelector) {
    return CUSTOM_INSIGHTS_FEEDBACK_SURVEY_OPTIONS;
  }

  return {
    ...CUSTOM_INSIGHTS_FEEDBACK_SURVEY_OPTIONS,
    position: "next_to_trigger" as NonNullable<
      DisplaySurveyPopoverOptions["position"]
    >,
    selector: options.anchorSelector,
  };
}

export const CUSTOM_INSIGHTS_EVENTS = {
  dashboardCreated: "Dashboard created",
  dashboardUpdated: "Dashboard updated",
  dashboardDeleted: "Dashboard deleted",
  dashboardViewed: "Dashboard viewed",
  insightCreated: "Insight created",
  insightUpdated: "Insight updated",
  insightDeleted: "Insight deleted",
  insightViewed: "Insight viewed",
} as const;

export type CustomInsightsAnalyticsSource = "manual" | "duplicate" | "ai_chat";

export type DashboardUpdateType =
  | "metadata"
  | "layout"
  | "both"
  | "add_insight"
  | "remove_insight"
  | "unknown";

type DashboardEventPayload = {
  dashboardId: string;
  source: CustomInsightsAnalyticsSource;
};

type DashboardUpdatePayload = DashboardEventPayload & {
  updateType: DashboardUpdateType;
};

type DashboardViewedPayload = {
  dashboardId: string;
  title: string;
  insightCount: number;
  description: string;
};

type InsightEventPayload = {
  insightId: string;
  source: CustomInsightsAnalyticsSource;
};

type InsightViewedPayload = {
  insightId: string;
  title: string;
  insightType: string;
  seriesCount: number;
  breakdownCount: number;
  hasDashboardContext: boolean;
  dashboardId?: string;
};

function hasOwnKey(value: Record<string, unknown>, key: string): boolean {
  return Object.hasOwn(value, key);
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function captureEvent(
  posthog: PostHogClient,
  event: string,
  properties: Record<string, EventPropertyValue>
) {
  posthog?.capture?.(event, properties);
}

export function getCustomInsightsFeedbackSurveyId(): string | null {
  const surveyId =
    process.env.NEXT_PUBLIC_CUSTOM_INSIGHTS_FEEDBACK_SURVEY_ID?.trim();
  return surveyId ? surveyId : null;
}

export function openCustomInsightsFeedbackSurvey(
  posthog: PostHogClient,
  surveyId = getCustomInsightsFeedbackSurveyId(),
  options?: OpenCustomInsightsFeedbackSurveyOptions
): boolean {
  if (!surveyId) {
    return false;
  }

  posthog?.displaySurvey?.(
    surveyId,
    buildCustomInsightsFeedbackSurveyOptions(options)
  );

  return true;
}

export function resolveDashboardUpdateType({
  hasMetadataChanges,
  hasLayoutChanges,
}: {
  hasMetadataChanges: boolean;
  hasLayoutChanges: boolean;
}): DashboardUpdateType | null {
  if (hasMetadataChanges && hasLayoutChanges) {
    return "both";
  }

  if (hasMetadataChanges) {
    return "metadata";
  }

  if (hasLayoutChanges) {
    return "layout";
  }

  return null;
}

export function inferDashboardUpdateTypeFromUpdates(
  updates: unknown
): DashboardUpdateType {
  const typedUpdates = toRecord(updates);
  if (!typedUpdates) {
    return "unknown";
  }

  const hasLayoutChanges = hasOwnKey(typedUpdates, "tiles");
  const hasMetadataChanges = [
    "title",
    "description",
    "default_time_range",
    "defaultTimeRange",
    "default_attribution_preset_id",
    "defaultAttributionPresetId",
  ].some((key) => hasOwnKey(typedUpdates, key));

  return (
    resolveDashboardUpdateType({
      hasMetadataChanges,
      hasLayoutChanges,
    }) ?? "unknown"
  );
}

export function getRecordString(value: unknown, key: string): string | null {
  const typedValue = toRecord(value);
  if (!typedValue) {
    return null;
  }

  const raw = typedValue[key];
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

export function captureDashboardCreated(
  posthog: PostHogClient,
  payload: DashboardEventPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.dashboardCreated, {
    source: payload.source,
    dashboard_id: payload.dashboardId,
  });
}

export function captureDashboardUpdated(
  posthog: PostHogClient,
  payload: DashboardUpdatePayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.dashboardUpdated, {
    source: payload.source,
    dashboard_id: payload.dashboardId,
    update_type: payload.updateType,
  });
}

export function captureDashboardDeleted(
  posthog: PostHogClient,
  payload: DashboardEventPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.dashboardDeleted, {
    source: payload.source,
    dashboard_id: payload.dashboardId,
  });
}

export function captureDashboardViewed(
  posthog: PostHogClient,
  payload: DashboardViewedPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.dashboardViewed, {
    dashboard_id: payload.dashboardId,
    dashboard_title: payload.title,
    insight_count: payload.insightCount,
    dashboard_description: payload.description,
  });
}

export function captureInsightCreated(
  posthog: PostHogClient,
  payload: InsightEventPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.insightCreated, {
    source: payload.source,
    insight_id: payload.insightId,
  });
}

export function captureInsightUpdated(
  posthog: PostHogClient,
  payload: InsightEventPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.insightUpdated, {
    source: payload.source,
    insight_id: payload.insightId,
  });
}

export function captureInsightDeleted(
  posthog: PostHogClient,
  payload: InsightEventPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.insightDeleted, {
    source: payload.source,
    insight_id: payload.insightId,
  });
}

export function captureInsightViewed(
  posthog: PostHogClient,
  payload: InsightViewedPayload
) {
  captureEvent(posthog, CUSTOM_INSIGHTS_EVENTS.insightViewed, {
    insight_id: payload.insightId,
    insight_title: payload.title,
    insight_type: payload.insightType,
    series_count: payload.seriesCount,
    breakdown_count: payload.breakdownCount,
    has_dashboard_context: payload.hasDashboardContext,
    ...(payload.dashboardId ? { dashboard_id: payload.dashboardId } : {}),
  });
}
